import { redactUrl } from './redact'

// Google Analytics 4 integration for SecondChance.dev
// Uses window.gtag loaded via script tag in index.html
export function initializeGA4() {
  // Only initialize in production
  if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
    console.log('[Analytics] GA4 disabled in development')
    return
  }

  // GA4 is loaded from index.html (secondchance.dev property G-F6J322X8R8).
  // If gtag already exists, defer to it rather than loading a second tag.
  if (window.gtag) {
    console.log('[Analytics] GA4 already loaded via index.html')
    return
  }

  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID
  if (!measurementId) {
    console.warn('[Analytics] GA4 Measurement ID not found')
    return
  }

  // Load GA4 script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || []
  function gtag() {
    dataLayer.push(arguments)
  }
  window.gtag = gtag

  gtag('js', new Date())
  gtag('config', measurementId, {
    // GA4 otherwise fires an automatic page_view using the raw browser URL,
    // which bypasses redactUrl() entirely and would ship a ?token= capability
    // to Analytics for anyone landing straight on /talent/sharing or
    // /reentry/verify. The router sends page views itself, redacted.
    send_page_view: false,

    // Privacy settings
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    
    // Custom dimensions
    custom_map: {
      'custom_parameter_1': 'user_type',
      'custom_parameter_2': 'registration_source'
    }
  })

  // Init is deferred ~1s after mount, so the router's first afterEach has
  // already fired and found no gtag. Send that first view here instead —
  // through trackPageView, so it is redacted like every other one.
  trackPageView(document.title, window.location.href)

  console.log('[Analytics] GA4 initialized')
}

// Track page views
export function trackPageView(page_title, page_location) {
  const location = redactUrl(page_location || window.location.href)
  // A referrer can carry the token too, when moving off the consent page.
  const referrer = redactUrl(document.referrer)

  if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
    console.log('[Analytics] Page view:', { page_title, page_location: location })
    return
  }

  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_title,
      page_location: location,
      page_referrer: referrer
    })
  }
}

// Track custom events
export function trackEvent(event_name, parameters = {}) {
  if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
    console.log('[Analytics] Event:', event_name, parameters)
    return
  }

  if (window.gtag) {
    window.gtag('event', event_name, {
      event_category: parameters.category || 'engagement',
      event_label: parameters.label,
      value: parameters.value,
      ...parameters
    })
  }
}

// Pre-defined conversion events
export const CONVERSION_EVENTS = {
  // User registration and onboarding
  SIGN_UP_STARTED: 'sign_up_started',
  SIGN_UP_COMPLETED: 'sign_up_completed',
  EMAIL_SIGNUP: 'email_signup',
  
  // Career assessment
  ASSESSMENT_STARTED: 'assessment_started',
  ASSESSMENT_COMPLETED: 'assessment_completed',
  
  // Application flows
  EARLY_ACCESS_REQUESTED: 'early_access_requested',
  MENTOR_APPLICATION_STARTED: 'mentor_application_started',
  MENTOR_APPLICATION_COMPLETED: 'mentor_application_completed',
  
  // Curriculum engagement
  CURRICULUM_VIEWED: 'curriculum_viewed',
  CURRICULUM_ENROLLED: 'curriculum_enrolled',
  MODULE_STARTED: 'module_started',
  MODULE_COMPLETED: 'module_completed',
  
  // Contact and support
  CONTACT_FORM_SUBMITTED: 'contact_form_submitted',
  SUPPORT_CHAT_STARTED: 'support_chat_started',
  
  // Content engagement
  BLOG_ARTICLE_READ: 'blog_article_read',
  RESOURCE_DOWNLOADED: 'resource_downloaded',
  
  // Social sharing
  CONTENT_SHARED: 'content_shared',
  
  // Navigation
  CTA_CLICKED: 'cta_clicked',
  EXTERNAL_LINK_CLICKED: 'external_link_clicked'
}

// Track specific conversion events
export function trackConversion(event_name, parameters = {}) {
  trackEvent(event_name, {
    ...parameters,
    event_category: 'conversion'
  })
}

// Track form interactions
export function trackFormEvent(form_name, action, additional_params = {}) {
  trackEvent(`form_${action}`, {
    category: 'form_interaction',
    label: form_name,
    form_name,
    action,
    ...additional_params
  })
}

// Track CTA clicks
export function trackCTAClick(cta_name, location, user_type = null) {
  trackEvent(CONVERSION_EVENTS.CTA_CLICKED, {
    category: 'cta_interaction',
    label: cta_name,
    cta_name,
    cta_location: location,
    user_type
  })
}

// Track user journey milestones
export function trackUserJourney(milestone, user_data = {}) {
  trackEvent('user_journey', {
    category: 'user_progress',
    label: milestone,
    milestone,
    user_type: user_data.type,
    registration_source: user_data.source,
    days_since_signup: user_data.daysSinceSignup
  })
}

// Track business metrics
export function trackBusinessMetric(metric_name, value, currency = 'USD') {
  trackEvent('business_metric', {
    category: 'business',
    label: metric_name,
    metric_name,
    value,
    currency
  })
}

// Enhanced ecommerce tracking (for future paid features)
export function trackPurchase(transaction_id, items = [], value = 0, currency = 'USD') {
  if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
    console.log('[Analytics] Purchase:', { transaction_id, items, value, currency })
    return
  }

  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id,
      value,
      currency,
      items: items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        quantity: item.quantity || 1,
        price: item.price
      }))
    })
  }
}

// Track search queries
export function trackSearch(search_term, results_count = null) {
  trackEvent('search', {
    category: 'site_search',
    label: search_term,
    search_term,
    results_count
  })
}

// Track video engagement
export function trackVideoEvent(video_title, action, progress = null) {
  trackEvent('video_engagement', {
    category: 'video',
    label: `${video_title} - ${action}`,
    video_title,
    action, // 'play', 'pause', 'complete', '25%', '50%', '75%'
    progress
  })
}

// Track file downloads
export function trackDownload(file_name, file_type, user_type = null) {
  trackEvent('file_download', {
    category: 'downloads',
    label: file_name,
    file_name,
    file_type,
    user_type
  })
}

// Set user properties
export function setUserProperties(properties) {
  if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
    console.log('[Analytics] User properties:', properties)
    return
  }

  if (window.gtag) {
    window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
      user_properties: properties
    })
  }
}

// Track errors and exceptions
export function trackError(error_description, fatal = false) {
  trackEvent('exception', {
    category: 'errors',
    description: error_description,
    fatal
  })
}
export { redactUrl }
